/**
 * PENG-3630: the one place that turns a content thumbnail into an imgix URL.
 *
 * Editors crop the thumbnail in the Ark (nova `ImageEdit`), which saves the
 * crop as `image.imgixCropParams`: `fp-x` / `fp-y` (the focal point, 0-1) and
 * `fp-z` (zoom). The API stores and returns them, but every card used to build
 * its URL as `imgUrl + 'fit=crop&w=..&h=..'`, so imgix centre-cropped and the
 * editor's crop never showed. Passing the focal point along makes each card
 * crop around it instead, the way the Ark preview does.
 *
 * An image uploaded but never cropped carries `{ fit, crop: 'focalpoint' }`
 * with no coordinates; it gets no focal params and stays centre-cropped.
 */

/**
 * The thumbnail's ratio as a CSS `aspect-ratio`. Editors upload and crop at
 * 16:9, so a box of this shape shows the whole crop.
 */
export const THUMBNAIL_ASPECT_RATIO = '16 / 9'

/**
 * imgix sizes that several surfaces share, about 2x the box each one fills so
 * the image stays sharp on high-density screens.
 */
export const THUMBNAIL_SIZES = {
  // 16:9 cards about 300px wide: Featured vertical cards, Discover more,
  // Explore portrait cards, the mobile carousel and /welcome
  CARD: { width: 640, height: 360 },
  // The home's related and videos cards (327px wide on phones)
  HOME_CARD: { width: 327, height: 184 },
  // 53x40 list thumbnails: playlist navigation and the retention panel
  LIST: { width: 106, height: 80 }
}

const toNumber = value => {
  // Number('') and Number(null) are 0, which would pin the crop to a corner.
  if (value === '' || value === null || value === undefined) return NaN
  return Number(value)
}

/**
 * Stored imgUrls end with '?' (see content-row.js), which is why params are
 * appended with no separator. Anything else gets the separator it needs, so a
 * second '?' never turns the first param into '?fit'.
 */
const withQuery = url => {
  if (/[?&]$/.test(url)) return url
  return url.includes('?') ? `${url}&` : `${url}?`
}

/** The editor's crop as imgix params, or '' when the image was never cropped. */
export const focalPointParams = image => {
  const params = image?.imgixCropParams
  const fpX = toNumber(params?.['fp-x'])
  const fpY = toNumber(params?.['fp-y'])
  if (!Number.isFinite(fpX) || !Number.isFinite(fpY)) return ''

  const fpZ = toNumber(params['fp-z'])
  const rot = toNumber(params.rot)

  return [
    'crop=focalpoint',
    `fp-x=${fpX}`,
    `fp-y=${fpY}`,
    Number.isFinite(fpZ) && fpZ > 1 && `fp-z=${fpZ}`,
    Number.isFinite(rot) && rot !== 0 && `rot=${rot}`
  ].filter(Boolean).join('&')
}

/**
 * The thumbnail URL with the editor's crop applied, ready for size params to
 * be appended (it always ends in '?' or '&'). For callers that add their own
 * sizing, like the og:image tag. Undefined when there is no image.
 */
export const getContentImageBase = image => {
  if (!image?.imgUrl) return undefined
  const focal = focalPointParams(image)
  return withQuery(image.imgUrl) + (focal ? `${focal}&` : '')
}

/**
 * The thumbnail cropped to `width` x `height` around the editor's focal point,
 * or `fallback` when the content has no image. Ask for the ratio of the box the
 * image is shown in, so CSS doesn't crop it a second time.
 */
export const getContentImageUrl = (image, { width, height, fallback } = {}) => {
  const base = getContentImageBase(image)
  if (!base) return fallback
  return `${base}fit=crop&w=${width}&h=${height}`
}
